import React, { useEffect, useRef, useState } from "react";
import {
  Cartesian3,
  Ion,
  Viewer,
  WebMapTileServiceImageryProvider,
  Credit,
  Cesium3DTileset,
  Matrix4,
  RequestScheduler,
  Request,
  HeadingPitchRange,
  EasingFunction,
  Color,
  PointPrimitiveCollection,
  PointPrimitive,
  PolylineCollection,
  Polyline,
  Material,
  Cartographic,
  EllipsoidGeodesic,
  Ellipsoid,
  HeightReference,
  SceneTransforms,
  ScreenSpaceEventType,
  Math,
  BoundingSphere,
  UrlTemplateImageryProvider,
  IonImageryProvider,
  OpenStreetMapImageryProvider,
  Rectangle,
  createDefaultImageryProviderViewModels,
} from "cesium";
import { Outlet } from "react-router-dom";
import {
  useAreaGenerateStore,
  useCesiumStore,
  useSearchStore,
  useTilesetStore,
} from "../Store/store";
import Tooltip from "../components/CesiumUtil/Tooltip";

export default function CesiumJS() {
  const {
    tooltip,
    setTooltip,
    setDeletePoint,
    setClearAllPoints,
    setAllPoints,
  } = useCesiumStore();

  const dragStateRef = useRef({
    isDragging: false,
    draggedPoint: null,
    originalPosition: null,
  });
  const cesiumContainerRef = useRef(null);
  const viewerRef = useRef(null);
  const tilesetRef = useRef(null);
  const rotationAnimationRef = useRef(null);
  const pointCollectionRef = useRef(null);
  const { isTilesetLoaded, setIsTilesetLoaded, tilesetUrl } = useTilesetStore();
  const { ViwerState, setViwerState } = useTilesetStore();
  const { searchResult } = useSearchStore();
  const { loadedServerPolygon, setLoadedServerPolygon } =
    useAreaGenerateStore();
  // 폴리라인 추가 함수
  const points = useRef([]);
  const polylineRef = useRef(null);
  const polygonEntity = useRef(null);
  const { setStopRatationFunction } = useCesiumStore();

  const { selectedArea } = useAreaGenerateStore();
  const allPoints = useCesiumStore.getState().allPoints;
  // 서버 좌표를 Cesium 좌표로 변환
  const convertServerCoordsToCesium = (serverCoords) => {
    return serverCoords.map((coord) =>
      Cartesian3.fromDegrees(coord.x, coord.y, coord.z || 0)
    );
  };

  // 서버 좌표를 포인트 컬렉션에 추가
  const createPointsFromServerData = (selectedArea) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const cesiumCoords = convertServerCoordsToCesium(selectedArea.coordinates);

    if (!pointCollectionRef.current) {
      pointCollectionRef.current = viewer.scene.primitives.add(
        new PointPrimitiveCollection()
      );
    }

    cesiumCoords.forEach((coord, index) => {
      const point = pointCollectionRef.current.add({
        position: coord,
        pixelSize: 15,
        color: Color.WHITE,
        outlineColor: Color.fromCssColorString(
          selectedArea.polygonColor ? selectedArea.polygonColor : "#ff1493"
        ),
        outlineWidth: 3,
        id: {
          type: "draw-point",
          index: index,
          sequence: selectedArea.coordinates[index].sequence,
          longitude: selectedArea.coordinates[index].x,
          latitude: selectedArea.coordinates[index].y,
        },
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      });
    });

    viewer.scene.requestRender();
    return pointCollectionRef.current;
  };

  // 서버 좌표를 폴리라인 컬렉션에 추가
  const createPolylineFromServerData = (selectedArea) => {
    const viewer = viewerRef.current;
    const cesiumCoords = convertServerCoordsToCesium(selectedArea.coordinates);

    const polylineCollection = new PolylineCollection();

    polylineCollection.add({
      positions: cesiumCoords,
      width: 4,
      material: Material.fromType("Color", {
        color: Color.fromCssColorString(
          selectedArea.polygonColor ? selectedArea.polygonColor : "#ff1493"
        ),
      }),
      clampToGround: true,
      id: {
        type: "draw-polyline",
      },
    });
    viewerRef.current.scene.primitives.add(polylineCollection);
    polylineRef.current = polylineCollection;
    return polylineCollection;
  };

  // 서버 좌표를 폴리곤 컬렉션에 추가
  const createPolygonFromServerData = (selectedArea, coordinates) => {
    const cesiumCoords = convertServerCoordsToCesium(coordinates);
    const viewer = viewerRef.current;

    if (polylineRef.current) {
      viewer.scene.primitives.remove(polylineRef.current);
      polylineRef.current = null;
    }

    // 2. 기존 폴리곤 삭제
    if (polygonEntity.current) {
      viewer.entities.remove(polygonEntity.current);
      polygonEntity.current = null;
    }
    const polygon = viewer.entities.add({
      polygon: {
        hierarchy: cesiumCoords,
        material: Color.fromCssColorString(
          selectedArea.polygonColor ? selectedArea.polygonColor : "#ff1493"
        ).withAlpha(0.25),
        clampToGround: true,
      },
    });

    polygonEntity.current = polygon;

    const polylineCollection = new PolylineCollection();
    polylineCollection.add({
      positions: cesiumCoords,
      width: 4,
      material: Material.fromType("Color", {
        color: Color.fromCssColorString(
          selectedArea.polygonColor ? selectedArea.polygonColor : "#ff1493"
        ),
      }),
      clampToGround: true,
    });

    viewerRef.current.scene.primitives.add(polylineCollection);
    polylineRef.current = polylineCollection;

    viewer.scene.requestRender();
    return polygon;
  };

  // 서버 좌표를 포인트 컬렉션과 폴리라인 컬렉션, 폴리곤 컬렉션에 추가
  const loadServerPolygon = (selectedArea) => {
    const viewer = viewerRef.current;

    if (viewer && viewer.scene.scene) {
      clearAllPoints();
    }

    let coordinates = selectedArea.coordinates;

    if (selectedArea.geometryType === "POLYGON") {
      coordinates = [...selectedArea.coordinates, selectedArea.coordinates[0]];
    }

    const pointCollection = createPointsFromServerData(selectedArea);
    const polyline = createPolylineFromServerData(selectedArea);

    let polygon = null;
    if (selectedArea.geometryType === "POLYGON") {
      polygon = createPolygonFromServerData(selectedArea, coordinates);
    }

    const allPoints = selectedArea.coordinates.map((coord, index) => ({
      latitude: coord.y,
      longitude: coord.x,
      index: coord.sequence,
    }));

    useCesiumStore.getState().setAllPoints(allPoints);

    viewer.scene.requestRender();

    const positions = coordinates.map((coord) =>
      Cartesian3.fromDegrees(coord.x, coord.y)
    );
    const boundingSphere = BoundingSphere.fromPoints(positions);

    // 카메라 이동 추가
    viewer.camera.flyToBoundingSphere(boundingSphere, {
      duration: 2.0,
      offset: new HeadingPitchRange(0, -0.5, 0), // 마지막 0은 거리 자동 계산
    });

    return { pointCollection, polyline };
  };

  useEffect(() => {
    setLoadedServerPolygon(loadServerPolygon);
  }, [setLoadedServerPolygon]);

  // ------------------------------------------------------------

  // 포인트 사이 거리 계산 함수
  function lengthGeodesicOnEllipsoid(positions) {
    const ellipsoid = Ellipsoid.WGS84;
    let sum = 0;
    const toCarto = (p) => ellipsoid.cartesianToCartographic(p);

    for (let i = 1; i < positions.length; i++) {
      const c0 = toCarto(positions[i - 1]);
      const c1 = toCarto(positions[i]);
      // 고도는 무시하고(= 표면 거리), 위경도만 사용
      c0.height = 0;
      c1.height = 0;
      const g = new EllipsoidGeodesic(c0, c1);
      sum += g.surfaceDistance; // meters
    }
    return sum;
  }
  // 폴리곤 면적 계산 함수
  function calculatePolygonArea(positions) {
    if (positions.length < 3) return 0;

    const cartographics = positions.map((pos) =>
      Cartographic.fromCartesian(pos)
    );

    // 평균 위도 계산 (코사인 보정용)
    const avgLat =
      cartographics.reduce((sum, c) => sum + c.latitude, 0) /
      cartographics.length;

    let area = 0;
    const n = cartographics.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const lon1 = cartographics[i].longitude;
      const lat1 = cartographics[i].latitude;
      const lon2 = cartographics[j].longitude;
      const lat2 = cartographics[j].latitude;

      area += (lon2 - lon1) * (lat2 + lat1);
    }

    area = window.Math.abs(area) / 2;

    // 라디안을 미터로 변환 (위도 보정 포함)
    const EARTH_RADIUS = 6371000;
    const areaInSquareMeters =
      area * EARTH_RADIUS * EARTH_RADIUS * window.Math.cos(avgLat);

    return areaInSquareMeters;
  }
  // 폴리곤 완성 함수
  const closePolygon = () => {
    const viewer = viewerRef.current;

    // 1. 기존 폴리라인 삭제 (중요!)
    if (polylineRef.current) {
      viewer.scene.primitives.remove(polylineRef.current);
      polylineRef.current = null;
    }

    // 2. 기존 폴리곤 삭제
    if (polygonEntity.current) {
      viewer.entities.remove(polygonEntity.current);
      polygonEntity.current = null;
    }

    // 좌표 닫기
    points.current.push(points.current[0]);

    const poly = viewer.entities.add({
      polygon: {
        hierarchy: points.current,
        material: Color.fromCssColorString(
          useAreaGenerateStore.getState().isAreaGenerateMode()
            ? useAreaGenerateStore.getState().selectedAreaColor
            : "#ff1493"
        ).withAlpha(0.25),
        clampToGround: true, // ← outline 미지원. 하지만 면은 지형에 착 달라붙음
      },
    });

    polygonEntity.current = poly;

    const polylineCollection = new PolylineCollection();
    polylineCollection.add({
      positions: points.current,
      width: 4,
      material: Material.fromType("Color", {
        color: Color.fromCssColorString(
          useAreaGenerateStore.getState().isAreaGenerateMode()
            ? useAreaGenerateStore.getState().selectedAreaColor
            : "#ff1493"
        ),
      }),
      clampToGround: true,
    });

    viewerRef.current.scene.primitives.add(polylineCollection);
    polylineRef.current = polylineCollection;

    viewer.scene.requestRender();

    const areaInSquareMeters = calculatePolygonArea(points.current);
    const areaInSquareKm = (areaInSquareMeters / 1000000).toFixed(4);

    useCesiumStore.getState().setPolygonArea(areaInSquareKm);
  };

  // 포인트 추가 및 연결 함수
  const addPointAndConnect = (position) => {
    addPoint(position);

    // 포인트 배열에 추가
    points.current.push(position);

    // 기존 선 제거
    if (polylineRef.current) {
      viewerRef.current.scene.primitives.remove(polylineRef.current);
    }

    // 기존 폴리곤 제거
    if (polygonEntity.current) {
      viewerRef.current.entities.remove(polygonEntity.current);
    }

    // 선 추가
    if (points.current.length >= 2) {
      const polylineCollection = new PolylineCollection();
      polylineCollection.add({
        positions: points.current,
        width: 4,
        material: Material.fromType("Color", {
          color: Color.fromCssColorString(
            useAreaGenerateStore.getState().isAreaGenerateMode()
              ? useAreaGenerateStore.getState().selectedAreaColor
              : "#ff1493"
          ),
        }),
        clampToGround: true,
      });

      viewerRef.current.scene.primitives.add(polylineCollection);
      polylineRef.current = polylineCollection;
    }
  };

  // 포인트 추가 함수
  const addPoint = (position) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    // 포인트 컬렉션이 없다면 생성
    if (!pointCollectionRef.current) {
      pointCollectionRef.current = viewer.scene.primitives.add(
        new PointPrimitiveCollection()
      );
    }

    const point = pointCollectionRef.current.add({
      position: position,
      pixelSize: 15,
      color: Color.WHITE,
      outlineColor: Color.fromCssColorString(
        useAreaGenerateStore.getState().isAreaGenerateMode()
          ? useAreaGenerateStore.getState().selectedAreaColor
          : "#ff1493"
      ),
      outlineWidth: 3,
      id: {
        type: "draw-point",
        index: points.current.length,
        longitude:
          (Cartographic.fromCartesian(position).longitude * 180) / Math.PI,
        latitude:
          (Cartographic.fromCartesian(position).latitude * 180) / Math.PI,
      },
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    });

    viewer.scene.requestRender();

    return point;
  };

  // 포인트 삭제 함수
  const deletePonint = (index) => {
    const viewer = viewerRef.current;
    if (!viewer || !pointCollectionRef.current) return;

    const pointCollection = pointCollectionRef.current;
    let pointToDelete = null;

    for (let i = 0; i < pointCollection.length; i++) {
      const point = pointCollection.get(i);

      if (point.id?.type === "draw-point" && point.id.index === index) {
        pointToDelete = point;
        break;
      }
    }

    if (pointToDelete) {
      pointCollectionRef.current.remove(pointToDelete);

      points.current.splice(index, 1);

      const pointCollection = pointCollectionRef.current;
      for (let i = 0; i < pointCollection.length; i++) {
        const point = pointCollection.get(i);
        if (point.id?.type === "draw-point" && point.id.index > index) {
          point.id.index = i; // 인덱스 재정렬
        }
      }
      updatePolyline();
      viewer.scene.requestRender();
    }
  };

  // 폴리라인 업데이트 함수
  const updatePolyline = () => {
    if (polylineRef.current) {
      viewerRef.current.scene.primitives.remove(polylineRef.current);
    }

    if (points.current.length >= 2) {
      const polylineCollection = new PolylineCollection();
      polylineCollection.add({
        positions: points.current,
        width: 4,
        material: Material.fromType("Color", {
          color: Color.fromCssColorString(
            useAreaGenerateStore.getState().isAreaGenerateMode()
              ? useAreaGenerateStore.getState().selectedAreaColor
              : "#ff1493"
          ),
        }),
        clampToGround: true,
      });

      viewerRef.current.scene.primitives.add(polylineCollection);
      polylineRef.current = polylineCollection;
    }
  };

  // 모든 포인트 가져오기
  const getAllPoints = () => {
    if (!pointCollectionRef.current) return;

    const points = [];
    const collection = pointCollectionRef.current;

    for (let i = 0; i < collection.length; i++) {
      const point = collection.get(i);

      const cartographic = Cartographic.fromCartesian(point.position);

      points.push({
        index: point.id?.index,
        position: point.position,
        longitude: (cartographic.longitude * 180) / Math.PI,
        latitude: (cartographic.latitude * 180) / Math.PI,
        primitive: point,
      });
    }

    return points;
  };

  // 마우스 클릭 시 툴팁 표시
  const handlePointClick = (event) => {
    const currentEditMode = useCesiumStore.getState().editMode;
    if (currentEditMode) return;

    const viewer = viewerRef.current;

    if (!viewer || currentEditMode) return;
    stopRotation();
  };

  // 마우스 다운 이벤트 처리
  const handleMouseDown = (event) => {
    const currentEditMode = useCesiumStore.getState().editMode;
    if (!currentEditMode) return;

    const viwer = viewerRef.current;

    if (!viwer || !currentEditMode) return;

    const picked = viwer.scene.pick(event.position);

    if (picked && picked.id?.type === "draw-point") {
      dragStateRef.current = {
        isDragging: true,
        draggedPoint: picked,
        originalPosition: picked.primitive.position.clone(),
      };

      // 포인트 드래그 중 카메라 이동 방지
      viwer.scene.screenSpaceCameraController.enableRotate = false;
      viwer.scene.screenSpaceCameraController.enableZoom = false;
      viwer.scene.screenSpaceCameraController.enableTranslate = false;

      setTooltip({
        visible: false,
        x: 0,
        y: 0,
        content: null,
        pointPosition: null,
      });

      // 마우스 커서 변경
      viwer.canvas.style.cursor = "grabbing";
    }
  };

  // 마우스 모션 이벤트 처리
  const handleMouseMove = (event) => {
    const currentEditMode = useCesiumStore.getState().editMode;
    if (!currentEditMode) return;

    const viwer = viewerRef.current;
    // 드래그 상태 가져오기
    const dragState = dragStateRef.current;

    if (!dragState.isDragging || !dragState.draggedPoint) return;

    const newPosition = viwer.camera.pickEllipsoid(
      event.endPosition,
      viwer.scene.globe.ellipsoid
    );

    if (newPosition) {
      dragState.draggedPoint.primitive.position = newPosition;

      const cartographic = Cartographic.fromCartesian(newPosition);

      dragState.draggedPoint.id.longitude =
        (cartographic.longitude * 180) / Math.PI;

      dragState.draggedPoint.id.latitude =
        (cartographic.latitude * 180) / Math.PI;

      const index = dragState.draggedPoint.id.index;
      points.current[index] = newPosition;

      updatePolyline();

      viwer.scene.requestRender();
    }
  };

  // 마우스 업 이벤트 처리
  const handleMouseUp = (event) => {
    const currentEditMode = useCesiumStore.getState().editMode;
    if (!currentEditMode) return;

    const viwer = viewerRef.current;
    const dragState = dragStateRef.current;

    if (!dragState.isDragging || !dragState.draggedPoint) return;

    // 카메라 컨트롤 복원
    viwer.scene.screenSpaceCameraController.enableRotate = true;
    viwer.scene.screenSpaceCameraController.enableZoom = true;
    viwer.scene.screenSpaceCameraController.enableTranslate = true;
    viwer.canvas.style.cursor = "default";

    // 드래그 좌표 업데이트
    const finalPosition = dragState.draggedPoint.primitive.position;
    const cartographic = Cartographic.fromCartesian(finalPosition);
    const longitude = (cartographic.longitude * 180) / Math.PI;
    const latitude = (cartographic.latitude * 180) / Math.PI;

    const index = dragState.draggedPoint.id.index;
    const currentAllPoints = useCesiumStore.getState().allPoints;

    const updatedPoints = [...currentAllPoints];
    updatedPoints[index] = {
      ...updatedPoints[index],
      longitude,
      latitude,
    };
    useCesiumStore.getState().setAllPoints(updatedPoints);

    dragStateRef.current = {
      isDragging: false,
      draggedPoint: null,
      originalPosition: null,
    };
  };

  // 클릭 이벤트 핸들러 (웨이포인트 추가)
  const handleDoubleClick = (event) => {
    // 영역 생성 모드에서만 좌표 찍기 가능: 이벤트 핸들러를 고려한 실시간 업데이트
    if (!useCesiumStore.getState().isAreaGenerateMode()) return;

    const viewer = viewerRef.current;
    if (!viewer) return;

    const picked = viewer.scene.pick(event.position);

    // 폴리곤 생성 모드 판별
    if (
      picked &&
      picked.id?.type === "draw-point" &&
      picked.id.index === 0 &&
      points.current.length >= 2 &&
      useAreaGenerateStore.getState().isAreaGenerateMode()
    ) {
      closePolygon();
      return;
    }

    // 클릭한 위치의 지구본 좌표 가져오기
    const pickedPosition = viewer.camera.pickEllipsoid(
      event.position,
      viewer.scene.globe.ellipsoid
    );
    let length = 0;
    if (pickedPosition) {
      // 포인트 추가
      addPointAndConnect(pickedPosition);
      const meters = lengthGeodesicOnEllipsoid(points.current);
      const km = meters / 1000;

      length = km.toFixed(2);
    }

    const allPoints = getAllPoints();
    const existingPoints = useCesiumStore.getState().allPoints || [];

    // 기존 데이터와 병합
    const updatedPoints = allPoints.map((point, idx) => ({
      ...point,
      length:
        idx < existingPoints.length ? existingPoints[idx]?.length : undefined,
    }));

    updatedPoints[updatedPoints.length - 1].length = length;
    useCesiumStore.getState().setAllPoints(updatedPoints);
  };

  // 포인트 초기화 함수
  const clearPoints = () => {
    if (pointCollectionRef.current) {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.scene.primitives.remove(pointCollectionRef.current);
      }
      pointCollectionRef.current = null;
    }
  };

  const clearAllPoints = () => {
    const viwer = viewerRef.current;
    // 1. Cesium 포인트 컬렉션 삭제
    clearPoints();
    // 2. 포인트 배열 초기화
    points.current = [];

    // 3. 폴리라인 삭제
    if (polylineRef.current) {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.scene.primitives.remove(polylineRef.current);
      }
      polylineRef.current = null;
    }

    // 4. 폴리곤 삭제
    if (polygonEntity.current) {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.entities.remove(polygonEntity.current);
      }
      polygonEntity.current = null;
    }

    // 5. Store 초기화
    setAllPoints([]);
    setTooltip({
      visible: false,
      x: 0,
      y: 0,
      content: null,
      pointPosition: null,
    });

    // 6. 화면 렌더링
    if (viwer) {
      viwer.scene.requestRender();
    }
  };

  // ------------------------------------------------------------

  // 자동 회전 정지 함수
  const stopRotation = () => {
    if (rotationAnimationRef.current) {
      cancelAnimationFrame(rotationAnimationRef.current);
      rotationAnimationRef.current = null;
    }
  };
  useEffect(() => {
    useCesiumStore.getState().setStopRatationFunction(stopRotation);
  }, [setStopRatationFunction]);

  // 지구 자동 회전 시작 함수
  const startEarthRotation = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    // 기존 회전 정지
    stopRotation();

    // 지구 전체 보기로 카메라 부드럽게 이동
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(0, 0, 20000000),
      duration: 2.0,
      easingFunction: EasingFunction.QUADRATIC_IN_OUT,
      complete: () => {
        // 이동 완료 후 자동 회전 시작
        const rotationSpeed = 0.0001;
        const rotateEarth = () => {
          viewer.camera.rotate(Cartesian3.UNIT_Z, rotationSpeed);
          rotationAnimationRef.current = requestAnimationFrame(rotateEarth);
        };
        rotateEarth();
      },
    });
  };

  // 타일셋 표시 함수
  const showTileset = async () => {
    const viewer = viewerRef.current;
    const tileset = tilesetRef.current;
    if (!viewer || !tileset) return;

    // 기존 회전 정지
    stopRotation();

    // 타일셋이 완전히 준비될 때까지 대기
    try {
      await tileset.readyPromise;
    } catch (error) {
      console.error("Tileset 로딩 실패:", error);
      return;
    }

    // 타일셋 추가 (이미 추가되어 있지 않은 경우만)
    if (!viewer.scene.primitives.contains(tileset)) {
      viewer.scene.primitives.add(tileset);
    }

    // boundingSphere 확인
    if (!tileset.boundingSphere) {
      console.warn("BoundingSphere가 아직 준비되지 않음");
      return;
    }
    // 타일 Z 위치 설정 (안전하게)
    tileset.modelMatrix = Matrix4.fromTranslation(new Cartesian3(0, 0, 5));

    const boundingSphere = tileset.boundingSphere;

    // 부드러운 카메라 이동
    viewer.camera.flyToBoundingSphere(
      boundingSphere,
      new HeadingPitchRange(0, -0.5, 0),
      {
        duration: 3.0,
        easingFunction: EasingFunction.QUADRATIC_IN_OUT,
      }
    );
  };

  // 타일셋 숨기기 함수
  const hideTileset = () => {
    const viewer = viewerRef.current;
    const tileset = tilesetRef.current;
    if (!viewer || !tileset) return;

    // 타일셋 제거
    viewer.scene.primitives.remove(tileset);

    // 지구 자동 회전 시작
    startEarthRotation();
  };

  const switchImageProvider = (layerType) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    let newProvider;

    switch (layerType) {
      case "satellite":
        newProvider = IonImageryProvider({ assetId: 1 });
        if (!Ion.defaultAccessToken) {
          console.error("Cesium Ion 토큰이 설정되지 않았습니다.");
        }
        break;

      case "cesium-world-terrain":
        newProvider = IonImageryProvider({ assetId: 1 });
        break;

      case "open-street-map":
        newProvider = OpenStreetMapImageryProvider({
          url: "https://a.tile.openstreetmap.org/",
        });
        break;

      case "carto-positron":
        newProvider = UrlTemplateImageryProvider({
          url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
          subdomains: ["a", "b", "c", "d"],
          credit: "© OpenStreetMap contributors © CARTO",
        });
        break;

      case "carto-dark":
        newProvider = UrlTemplateImageryProvider({
          url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
          subdomains: ["a", "b", "c", "d"],
          credit: "© OpenStreetMap contributors © CARTO",
        });
        break;

      case "stamen-terrain":
        newProvider = UrlTemplateImageryProvider({
          url: "https://stamen-tiles.a.ssl.fastly.net/terrain/{z}/{x}/{y}.png",
          credit:
            "Map tiles by Stamen Design, under CC BY 3.0. Data by OpenStreetMap, under ODbL.",
        });
        break;

      case "stamen-toner":
        newProvider = UrlTemplateImageryProvider({
          url: "https://stamen-tiles.a.ssl.fastly.net/toner/{z}/{x}/{y}.png",
          credit:
            "Map tiles by Stamen Design, under CC BY 3.0. Data by OpenStreetMap, under ODbL.",
        });
        break;

      case "vworld-satellite":
        const API_KEY = process.env.REACT_APP_VWORLD_API_KEY;
        newProvider = new WebMapTileServiceImageryProvider({
          url: `/req/wmts/1.0.0/${API_KEY}/Satellite/{TileMatrix}/{TileRow}/{TileCol}.jpeg`,
          layer: "Satellite",
          style: "default",
          format: "image/jpeg",
          tileMatrixSetID: "GoogleMapsCompatible",
          minimumLevel: 6,
          maximumLevel: 19,
          rectangle: Rectangle.fromDegrees(112.5, 27.06, 140.625, 45.09),
          credit: new Credit("VWorld Korea"),
        });
        break;

      case "vworld-hybrid":
        newProvider = new WebMapTileServiceImageryProvider({
          url: `/req/wmts/1.0.0/${API_KEY}/Hybrid/{TileMatrix}/{TileRow}/{TileCol}.png`,
          layer: "Hybrid",
          style: "default",
          format: "image/png",
          tileMatrixSetID: "GoogleMapsCompatible",
          minimumLevel: 6,
          maximumLevel: 19,
          rectangle: Rectangle.fromDegrees(112.5, 27.06, 140.625, 45.09),
          credit: new Credit("VWorld Korea"),
        });
        break;

      default:
        newProvider = IonImageryProvider({ assetId: 1 });
    }

    viewer.imageryLayers.removeAll();
    viewer.imageryLayers.addImageryProvider(newProvider);
  };

  // 타일셋 로드 함수
  const loadTileset = async () => {
    try {
      // ✅ 런타임 환경변수 사용 (window._env_) 또는 빌드 시점 값 (process.env)
      const cesiumToken =
        window._env_?.REACT_APP_CESIUM_ION_TOKEN ||
        process.env.REACT_APP_CESIUM_ION_TOKEN;
      const vworldKey =
        window._env_?.REACT_APP_VWORLD_API_KEY ||
        process.env.REACT_APP_VWORLD_API_KEY;

      // ⚠ 디버깅용: 런타임/빌드 시점 env 값이 실제로 들어왔는지 확인
      console.log("test");

      // 액세스 토큰 설정
      Ion.defaultAccessToken = cesiumToken;

      // VWorld API 키 설정
      const API_KEY = vworldKey;
      const layer = "Satellite";
      const tileType = "jpeg";

      // VWorld WMTS 이미지 제공자 추가
      const vworldImagery = new WebMapTileServiceImageryProvider({
        url: `https://api.vworld.kr/req/wmts/1.0.0/${API_KEY}/${layer}/{TileMatrix}/{TileRow}/{TileCol}.${tileType}`,
        layer: layer,
        style: "default",
        format: tileType,
        tileMatrixSetID: "GoogleMapsCompatible",
        maximumLevel: 12,
        credit: new Credit("VWorld Korea"),
      });

      // Viewer 생성
      const viewer = new Viewer(cesiumContainerRef.current, {
        imageryProvider: vworldImagery,
        // UI 요소 비활성화
        animation: false,
        fullscreenButton: false,
        geocoder: false,
        homeButton: false,
        infoBox: false,
        sceneModePicker: false,
        selectionIndicator: false,
        timeline: false,
        navigationHelpButton: false,
        navigationInstructionsInitiallyVisible: false,
        shouldAnimate: false,
        vrButton: false,
        baseLayerPicker: true, // ControlDock에서 관리하므로 비활성화

        // 로고 제거 (Cesium ion 로딩 크레딧 제거)
        creditContainer: document.createElement("div"), // 빈 컨테이너로 대체
      });

      // 전역으로 viewer 설정 (ControlDock에서 접근 가능하도록)
      window.cesiumViewer = viewer;
      viewer.baseLayerPicker.container.style.display = "none";
      // 성능 최적화 설정
      viewer.scene.globe.enableLighting = false;
      viewer.scene.fog.enabled = false;
      viewer.scene.skyAtmosphere.show = false;
      viewer.scene.requestRenderMode = true;
      viewer.scene.maximumRenderTimeChange = Infinity;

      // 메모리 사용량 최적화
      viewer.scene.debugShowFramesPerSecond = false;
      viewer.scene.debugShowMemoryUsage = false;

      // 타일 로딩 최적화
      viewer.scene.globe.maximumScreenSpaceError = 2.0; // 기본값보다 낮게 설정
      viewer.scene.globe.tileCacheSize = 1000; // 타일 캐시 크기 증가

      // RequestScheduler 최적화
      RequestScheduler.requestsByServer = {
        output_tiles_optimized: 6, // 동시 요청 수 제한
      };

      // 3D Tiles 타일셋 로드 (최적화된 설정)
      let tileset = null;

      if (tilesetUrl && tilesetUrl.trim() !== "") {
        tileset = await Cesium3DTileset.fromUrl(
          // "/output_tiles_optimized/tileset.json",
          tilesetUrl,
          {
            maximumScreenSpaceError: 8, // 더 낮은 품질로 시작
            maximumMemoryUsage: 512, // 메모리 사용량 제한 (MB)
            cullWithChildrenBounds: false, // 성능 향상을 위해 비활성화
            dynamicScreenSpaceError: true, // 동적 화면 공간 오류 활성화
            dynamicScreenSpaceErrorDensity: 0.00278,
            dynamicScreenSpaceErrorFactor: 4.0,
            dynamicScreenSpaceErrorHeightFalloff: 0.25,
            preloadWhenHidden: false, // 숨겨진 타일 미리 로드 비활성화
            preferLeaves: true, // 리프 노드 우선 로딩
            progressiveResolutionHeightFraction: 0.5, // 점진적 해상도
          }
        );
      }

      // 타일셋 참조 저장
      tilesetRef.current = tileset;

      // 타일셋이 완전히 로드될 때까지 대기
      if (tileset) {
        await tileset.readyPromise;
      }
      // Viewer 참조 저장
      viewerRef.current = viewer;

      // 클릭 이벤트 리스너 추가
      viewer.screenSpaceEventHandler.setInputAction(
        handleDoubleClick,
        ScreenSpaceEventType.LEFT_DOUBLE_CLICK
      );
      viewer.screenSpaceEventHandler.setInputAction(
        handlePointClick,
        ScreenSpaceEventType.WHEEL
      );
      viewer.screenSpaceEventHandler.setInputAction(
        handleMouseDown,
        ScreenSpaceEventType.LEFT_DOWN
      );
      viewer.screenSpaceEventHandler.setInputAction(
        handleMouseMove,
        ScreenSpaceEventType.MOUSE_MOVE
      );
      viewer.screenSpaceEventHandler.setInputAction(
        handleMouseUp,
        ScreenSpaceEventType.LEFT_UP
      );

      // 초기 상태 설정
      if (isTilesetLoaded) {
        showTileset();
      } else {
        startEarthRotation();
      }

      // 타일셋 로딩 완료 후 품질 점진적 향상
      setTimeout(() => {
        if (tilesetRef.current) {
          tilesetRef.current.maximumScreenSpaceError = 4; // 품질 향상
        }
      }, 5000);
    } catch (e) {
      console.error("View 생성 실패: ", e);
    }
  };

  // 삭제 함수를 Store에 등록
  useEffect(() => {
    setDeletePoint(deletePonint);
  }, [setDeletePoint]);

  // 포인트 초기화 함수를 Store에 등록
  useEffect(() => {
    setClearAllPoints(clearAllPoints);
  }, [setClearAllPoints]);

  // 초기 설정 (한 번만 실행)
  useEffect(() => {
    // 기존 viewer 정리
    if (viewerRef.current) {
      viewerRef.current.destroy();
      viewerRef.current = null;
    }
    if (cesiumContainerRef.current) {
      cesiumContainerRef.current.innerHTML = "";
    }

    // 타일셋 로드
    loadTileset();

    // cleanup
    return () => {
      stopRotation();
      clearPoints();
      if (polygonEntity.current) {
        viewerRef.current.entities.remove(polygonEntity.current);
      }
      if (tilesetRef.current) {
        tilesetRef.current = null;
      }
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        try {
          viewerRef.current.screenSpaceEventHandler.removeInputAction(3);
          viewerRef.current.destroy();
        } catch (e) {
          // 개발모드/HMR에서 드물게 removeChild 충돌 시 무시 가능
          console.warn("Cesium destroy warning:", e);
        } finally {
          viewerRef.current = null;
        }
      }
    };
  }, []); // isTilesetLoaded 의존성 제거

  // 타일셋 전환 함수
  useEffect(() => {
    const switchTileset = async () => {
      if (!tilesetUrl || !viewerRef.current) return;

      try {
        if (tilesetRef.current) {
          viewerRef.current.scene.primitives.remove(tilesetRef.current);
          tilesetRef.current = null;
        }

        const newTileset = await Cesium3DTileset.fromUrl(tilesetUrl, {
          maximumScreenSpaceError: 8, // 더 낮은 품질로 시작
          maximumMemoryUsage: 512, // 메모리 사용량 제한 (MB)
          cullWithChildrenBounds: false, // 성능 향상을 위해 비활성화
          dynamicScreenSpaceError: true, // 동적 화면 공간 오류 활성화
          dynamicScreenSpaceErrorDensity: 0.00278,
          dynamicScreenSpaceErrorFactor: 4.0,
          dynamicScreenSpaceErrorHeightFalloff: 0.25,
          preloadWhenHidden: false, // 숨겨진 타일 미리 로드 비활성화
          preferLeaves: true, // 리프 노드 우선 로딩
          progressiveResolutionHeightFraction: 0.5, // 점진적 해상도
        });
        await newTileset.readyPromise;

        // 새 타일셋 추가
        viewerRef.current.scene.primitives.add(newTileset);
        tilesetRef.current = newTileset;

        // 카메라 이동
        const boundingSphere = newTileset.boundingSphere;

        // 부드러운 카메라 이동
        viewerRef.current.camera.flyToBoundingSphere(
          boundingSphere,
          new HeadingPitchRange(0, -0.5, 0),
          {
            duration: 3.0,
            easingFunction: EasingFunction.QUADRATIC_IN_OUT,
          }
        );
      } catch (error) {
        console.error("타일셋 전환 실패:", error);
      }
    };
    switchTileset();
  }, [tilesetUrl]);

  // isTilesetLoaded 상태 변화에 따른 동적 제어
  useEffect(() => {
    if (isTilesetLoaded) {
      showTileset();
    } else {
      hideTileset();
      startEarthRotation();
    }
  }, [isTilesetLoaded]);

  // 뷰어 상태 변화에 따른 동적 제어
  useEffect(() => {
    hideTileset();
    startEarthRotation();
  }, [ViwerState]);

  // 검색 결과 표시
  useEffect(() => {
    if (searchResult) {
      stopRotation();
      if (viewerRef.current) {
        viewerRef.current.camera?.flyTo({
          destination: Cartesian3.fromDegrees(
            searchResult.longitude, // 경도
            searchResult.latitude, // 위도
            300 // 높이 (미터)
          ),
          orientation: {
            heading: window.Cesium?.Math.toRadians(0), // 방향 (0도 = 북쪽)
            pitch: window.Cesium?.Math.toRadians(-45), // 피치 (-90도 = 아래, 90도 = 위)
            roll: 0, // 롤
          },
          duration: 3.0, // 애니메이션 시간
          easingFunction: EasingFunction.QUADRATIC_IN_OUT,
        });
      }
    }
  }, [searchResult]);

  return (
    <div style={{ width: "100%", height: "100vh", position: "relative" }}>
      {/* 툴팁 */}
      {tooltip.visible && (
        <Tooltip
          tooltip={tooltip}
          setTooltip={setTooltip}
          deletePonint={deletePonint}
        />
      )}
      <div
        ref={cesiumContainerRef}
        style={{ width: "100%", height: "100vh" }}
      />
    </div>
  );
}
